import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Tooltip } from '@material-ui/core';
import { FileCopy, OpenInNew } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import { FEAT_POINTS } from '@pvplevelup/core/dist/feats/FeatTypes';
import { decodeSpec, describeSpec, getFeatClass, getSpecClass, requiredLevel, spentPoints } from '@pvplevelup/core/dist/feats/FeatSpec';
import { classImages } from 'utilities/ClassImages';
import { FeatTrees } from './FeatTrees';
import { useFeatClasses } from './useFeatClasses';

const useStyles = makeStyles((theme) => ({
  summary: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gridColumnGap: theme.spacing(1.5),
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'auto minmax(0, 1fr)',
    },
  },
  classIcon: {
    width: 40,
    height: 40,
  },
  title: {
    fontWeight: 800,
    fontSize: '1.05rem',
  },
  sub: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.85rem',
  },
  actions: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridColumnGap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      gridColumn: '1 / -1',
      justifyContent: 'start',
      marginTop: theme.spacing(1),
    },
  },
  button: {
    textTransform: 'none',
    fontWeight: 700,
  },
  centered: {
    display: 'grid',
    placeItems: 'center',
    minHeight: 200,
    color: 'rgba(255, 255, 255, 0.6)',
  },
}));

interface Props {
  /** the spec string the game reports */
  spec: string | null | undefined;
}

/** A character's feat build, read only: which trees the points went into, and the trees themselves. */
export const FeatBuild: React.FC<Props> = ({spec}) => {
  const classes = useStyles();
  const history = useHistory();
  const featClasses = useFeatClasses();
  const [copied, setCopied] = useState(false);
  const data = featClasses && getFeatClass(featClasses, getSpecClass(spec));
  const ranks = useMemo(() => data && spec ? decodeSpec(data, spec).ranks : {}, [data, spec]);

  if(!spec)
    return <div className={classes.centered}>No feat build recorded yet. It's picked up while the app tracks a game.</div>;
  if(!featClasses)
    return <div className={classes.centered}><CircularProgress /></div>;
  if(!data)
    return <div className={classes.centered}>This feat build couldn't be read.</div>;

  const copy = () => {
    navigator.clipboard.writeText(spec).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  };
  return (
    <div>
      <div className={classes.summary}>
        <img className={classes.classIcon} src={classImages.getColorIcon(data.class)} alt={data.class} />
        <div style={{minWidth: 0}}>
          <div className={classes.title}>{describeSpec(data, ranks) || 'No points spent'}</div>
          <div className={classes.sub}>{data.class} · {spentPoints(ranks)} / {FEAT_POINTS} points · level {requiredLevel(data, ranks)}</div>
        </div>
        <div className={classes.actions}>
          <Tooltip title={copied ? 'Copied' : 'Copy the spec code'}>
            <Button className={classes.button} size="small" variant="outlined" startIcon={<FileCopy />} onClick={copy}>Copy</Button>
          </Tooltip>
          <Button className={classes.button} size="small" variant="outlined" color="primary" startIcon={<OpenInNew />}
            onClick={() => history.push(`/feats?spec=${encodeURIComponent(spec)}`)}>Open in planner</Button>
        </div>
      </div>
      <FeatTrees data={data} ranks={ranks} />
    </div>
  );
};
